"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BackpackEssentials() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const services = [{
    number: "01",
    category: "CREATIVITY",
    title: "Classroom Projects",
    description: "Explore the vibrant world of our student-led initiatives, where imagination meets hands-on learning in every corner of our bright classroom.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_projects_gallery.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_projects_gallery.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_projects_gallery.png-wvc-sizes",
    alt: "Students working together on colorful art projects at wooden tables"
  }, {
    number: "02",
    category: "LEARNING",
    title: "Reading Nooks",
    description: "Our cozy reading corners are designed to inspire a lifelong love for stories, providing a quiet sanctuary for every young reader to explore.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/reading_corner_sanctuary.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/reading_corner_sanctuary.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/reading_corner_sanctuary.png-wvc-sizes",
    alt: "A comfortable reading corner filled with books and soft cushions"
  }, {
    number: "03",
    category: "EXPRESSION",
    title: "Student Art Walls",
    description: "We celebrate every child's unique perspective by showcasing their masterpieces on our dedicated art walls, turning our room into a living gallery.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/student_art_display.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/student_art_display.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/student_art_display.png-wvc-sizes",
    alt: "A wall covered in colorful student paintings and drawings"
  }, {
    number: "04",
    category: "RESOURCES",
    title: "Daily Essentials",
    description: "Stay informed with our weekly schedules, lunch menus, and classroom policies to ensure a smooth and happy school day for your little one.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_daily_resources.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_daily_resources.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_daily_resources.png-wvc-sizes",
    alt: "A bulletin board displaying the weekly schedule and lunch menu"
  }];
  return <section data-section-id="4377" id="backpack-essentials" className="bg-background text-foreground py-32">
      {}
      <div ref={header.ref} className={`max-w-7xl mx-auto px-6 lg:px-12 mb-24 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <p className="font-mono text-xs tracking-[0.25em] uppercase mb-8" style={{
        color: "var(--chart-2)"
      }}>EXPLORE</p>
        <h2 className="font-sans font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-3xl text-foreground">
          Welcome to our <span className="italic font-light">Class 2A.</span>
        </h2>
      </div>

      {}
      <div className="flex flex-col">
        {services.map((service, i) => {
        const isEven = i % 2 === 0;
        return <ServiceRow key={i} data-index={i} service={service} isEven={isEven} isLast={i === services.length - 1} />;
      })}
      </div>
    </section>;
}
function ServiceRow({
  service,
  isEven,
  isLast,
  ...rest
}: {
  service: {
    number: string;
    category: string;
    title: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  isEven: boolean;
  isLast: boolean;
  [key: string]: unknown;
}) {
  const reveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest}>
      <div ref={reveal.ref} className={`py-20 transition-all duration-1000 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-0 items-center">
          {}
          <div className={`lg:col-span-6 ${isEven ? "lg:order-1 lg:pr-12" : "lg:order-2 lg:pl-12"}`}>
            <div className={`relative w-full overflow-hidden rounded-lg shadow-lg ${isEven ? "lg:-ml-6 xl:-ml-12" : "lg:-mr-6 xl:-mr-12"}`} style={{
            aspectRatio: "4 / 5"
          }}>
              <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>

          {}
          <div className={`lg:col-span-5 px-6 lg:px-0 ${isEven ? "lg:order-2 lg:col-start-8" : "lg:order-1 lg:col-start-2 lg:row-start-1"}`}>
            <div className="max-w-md">
              <p className="font-sans italic text-5xl mb-6 text-primary">
                {service.number}
              </p>
              <p className="font-mono text-xs tracking-[0.25em] uppercase mb-6" style={{
              color: "var(--chart-2)"
            }}>
                {service.category}
              </p>
              <h3 className="font-sans font-default text-3xl md:text-4xl leading-tight tracking-tight mb-6 text-foreground">
                {service.title}
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground mb-8 max-w-sm font-default">
                {service.description}
              </p>
              <Button asChild variant="link" className="px-0 h-auto font-mono text-xs tracking-[0.2em] uppercase text-foreground hover:text-primary">
                <Link to="/ParentToolboxPage">
                  Download Info <ArrowRight className="size-3" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {}
      {!isLast && <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="h-px w-full bg-border opacity-40" />
        </div>}
    </div>;
}